/**
 * AndDone Debug Logger
 *
 * Tagged namespace logger for tracing async message flows across the SDK,
 * Angular fragments, and SignalR hub.
 *
 * Usage:
 *   import { createLogger } from '@anddone/ajs-logger';
 *   const logger = createLogger('SDK', 'Fragments', 'Card');
 *   logger.info('handshake', 'Request received', { fragmentName });
 *
 * Enable via:
 *   - Build flag:      __ANDDONE_DEBUG__ = true  (Vite define / Angular environment)
 *   - Runtime toggle:  localStorage.setItem('__ANDDONE_DEBUG__', 'true')
 *
 * Format: [AJS:{Project}:{Module}:{Event}] message  {data?}
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type LogLevel = 'info' | 'warn' | 'error' | 'debug';

export interface AjfLogger {
  info(event: string, message: string, data?: unknown): void;
  warn(event: string, message: string, data?: unknown): void;
  error(event: string, message: string, data?: unknown): void;
  debug(event: string, message: string, data?: unknown): void;
  group(event: string, label: string, fn: () => void): void;
  time(event: string, label: string): void;
  timeEnd(event: string, label: string, data?: unknown): void;
}

// ─── Environment gate ─────────────────────────────────────────────────────────

declare const __ANDDONE_DEBUG__: boolean | undefined;

/**
 * Checks all three gates in priority order:
 * 1. Build-time flag (zero-cost in prod bundles when false)
 * 2. Runtime localStorage override (useful for diagnosing deployed envs)
 * 3. Explicit programmatic enable (for Angular environment.ts injection)
 */

function isEnabled(): boolean {
  // Build-time flag
  try {
    if (typeof __ANDDONE_DEBUG__ !== 'undefined' && __ANDDONE_DEBUG__ === true) {
      return true;
    }
  } catch {
    // fall through
  }

  // Runtime global flag - useful for Angular environment files
  try {
    if ((globalThis as { __ANDDONE_DEBUG__?: boolean }).__ANDDONE_DEBUG__ === true) {
      return true;
    }
  } catch {
    // fall through
  }

  // Runtime localStorage override - must be set in iframe origin
  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('__ANDDONE_DEBUG__') === 'true';
    }
  } catch {
    // fall through
  }

  return false;
}

// ─── Colour palette (console ANSI-style via %c) ───────────────────────────────

const LEVEL_STYLES: Record<LogLevel, string> = {
  info: 'color: #4fc3f7; font-weight: 600;',
  warn: 'color: #ffb74d; font-weight: 600;',
  error: 'color: #ef5350; font-weight: 700;',
  debug: 'color: #b0bec5; font-weight: 400;',
};

const PROJECT_STYLE = 'color: #ffd54f; font-weight: 700;';
const MODULE_STYLE = 'color: #81c784; font-weight: 700;';
const EVENT_STYLE = 'color: #ce93d8; font-weight: 600;';
const RESET_STYLE = 'color: inherit; font-weight: normal;';

// ─── Tag builder ──────────────────────────────────────────────────────────────

function buildTag(project: string, module: string, event: string): string {
  return `[AJS:${project}:${module}:${event}]`;
}

// Active timers: key = `${project}:${module}:${event}:${label}`
const activeTimers = new Map<string, number>();

// ─── Factory ──────────────────────────────────────────────────────────────────

/**
 * Create a logger scoped to a specific project and module.
 *
 * @param project - Short project identifier shown in every log tag, e.g. 'SDK', 'Fragments', 'API'
 * @param module - Short module identifier shown in every log tag, e.g. 'Card', 'Orchestrator', 'SignalR'
 * @returns AjfLogger
 *
 * @example
 * const logger = createLogger('SDK', 'Fragments', 'Card');
 * logger.info('mount', 'Mounting fragment', { fragmentName, elementId });
 * logger.warn('submit', 'Ignored - already in progress');
 * logger.group('submit-fragment', 'Sending to all fragments', () => {
 *   fragments.forEach(f => logger.info('submit-fragment', `→ ${f.name}`, { status: f.status }));
 * });
 * logger.time('handshake', 'round-trip');
 * // ... async work ...
 * logger.timeEnd('handshake', 'round-trip', { fragmentName });
 */
export function createLogger(project: string, module: string): AjfLogger {
  // Re-evaluate on every call so runtime localStorage changes take effect
  // without needing to recreate the logger instance.
  function enabled(): boolean {
    return isEnabled();
  }

  function log(level: LogLevel, event: string, message: string, data?: unknown): void {
    if (!enabled()) return;

    const tag = buildTag(project, module, event);
    const consoleFn =
      level === 'error'
        ? console.error
        : level === 'warn'
          ? console.warn
          : level === 'debug'
            ? console.debug
            : console.log;

    if (data !== undefined) {
      consoleFn(
        `%c${tag}%c %c${message}%c`,
        PROJECT_STYLE + MODULE_STYLE + EVENT_STYLE,
        RESET_STYLE,
        LEVEL_STYLES[level],
        RESET_STYLE,
        data,
      );
    } else {
      consoleFn(
        `%c${tag}%c %c${message}%c`,
        PROJECT_STYLE + MODULE_STYLE + EVENT_STYLE,
        RESET_STYLE,
        LEVEL_STYLES[level],
        RESET_STYLE,
      );
    }
  }

  return {
    info(event, message, data?) {
      log('info', event, message, data);
    },

    warn(event, message, data?) {
      log('warn', event, message, data);
    },

    error(event, message, data?) {
      log('error', event, message, data);
    },

    debug(event, message, data?) {
      log('debug', event, message, data);
    },

    group(event: string, label: string, fn: () => void): void {
      if (!enabled()) {
        fn();
        return;
      }

      const tag = buildTag(project, module, event);

      console.groupCollapsed(
        `%c${tag}%c ${label}`,
        PROJECT_STYLE + MODULE_STYLE + EVENT_STYLE,
        RESET_STYLE,
      );

      try {
        fn();
      } finally {
        console.groupEnd();
      }
    },

    time(event: string, label: string): void {
      if (!enabled()) return;

      const key = `${project}:${module}:${event}:${label}`;

      activeTimers.set(key, performance.now());
      log('debug', event, `⏱ timer start — ${label}`);
    },

    timeEnd(event: string, label: string, data?: unknown): void {
      if (!enabled()) return;

      const key = `${project}:${module}:${event}:${label}`;
      const start = activeTimers.get(key);

      if (start === undefined) {
        log('warn', event, `⏱ timeEnd called but no matching time() found — ${label}`);
        return;
      }

      const ms = (performance.now() - start).toFixed(2);

      activeTimers.delete(key);
      log('info', event, `⏱ ${label} completed in ${ms}ms`, data);
    },
  };
}
